import type { GuestRequest } from '@/types';

/** Epoch millis of a request's latest notice to the guest, or 0 when there is none. */
export function noticeMillis(request: Pick<GuestRequest, 'guestNotification'>): number {
  const at = (request.guestNotification as { at?: { toMillis?: () => number } } | null)?.at;
  return typeof at?.toMillis === 'function' ? at.toMillis() : 0;
}

/**
 * The requests whose notice is newer than the last snapshot saw.
 *
 * `previous === null` is the baseline (first snapshot after a reload) and
 * yields nothing — otherwise every open request would re-announce itself on
 * each visit. A request that appears for the first time *after* the baseline
 * (the guest just filed it) also stays quiet: its notice, if any, is not news.
 */
export function newNotices<T extends Pick<GuestRequest, 'id' | 'guestNotification'>>(
  previous: ReadonlyMap<string, number> | null,
  next: readonly T[],
): T[] {
  if (previous === null) return [];
  return next.filter((request) => {
    if (!request.guestNotification) return false;
    const at = noticeMillis(request);
    const before = previous.get(request.id);
    return before !== undefined && at > before;
  });
}

/** How many requests carry a notice the guest has not looked at since `seenAt`. */
export function unreadNoticeCount(
  requests: readonly Pick<GuestRequest, 'guestNotification'>[] | null,
  seenAt: number,
): number {
  return (requests ?? []).filter((request) => noticeMillis(request) > seenAt).length;
}
